import React, { useState, useRef, useMemo } from "react";
import {
  UploadCloud,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  X,
  Loader2,
  Table,
  Check,
  Info,
  Layers,
  ArrowRight,
} from "lucide-react";
import {
  parseUniversalFile,
  toCsv,
  downloadCsv,
  downloadExcel,
  mapRowHeaders,
  CSV_SECTIONS,
} from "../../utils/csvHelper";
import { useToast } from "../../hooks/use-toast";

const CsvUploadModal = ({
  isOpen,
  onClose,
  sectionKey = "courses",
  onImport,
  allowModeSwitch = true,
  customTitle,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importMode, setImportMode] = useState("append"); // "append" or "replace"
  const [showAllErrors, setShowAllErrors] = useState(false);

  const schema = CSV_SECTIONS[sectionKey] || CSV_SECTIONS.courses;

  // Handle Excel template download (.xlsx)
  const handleDownloadExcelTemplate = () => {
    try {
      const filename = (schema.filename || `${sectionKey}_template.csv`).replace(/\.csv$/, ".xlsx");
      downloadExcel(filename, schema.sampleRows, schema.columns, schema.badge || "Template");
      toast({
        title: "Excel Template Downloaded",
        description: `Saved ${filename} with sample data and column headers.`,
      });
    } catch (err) {
      toast({
        title: "Download Failed",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  // Handle CSV template download (.csv)
  const handleDownloadCsvTemplate = () => {
    try {
      const csvStr = toCsv(schema.sampleRows, schema.columns);
      downloadCsv(schema.filename, csvStr);
      toast({
        title: "CSV Template Downloaded",
        description: `Saved ${schema.filename} to your computer.`,
      });
    } catch (err) {
      toast({
        title: "Download Failed",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  // Reset file state
  const handleReset = () => {
    setFile(null);
    setRawRows([]);
    setIsReadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Process file (Excel, CSV, TSV, JSON)
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) processSelectedFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) processSelectedFile(dropped);
  };

  const processSelectedFile = async (f) => {
    const name = f.name.toLowerCase();
    const isSupported =
      name.endsWith(".xlsx") ||
      name.endsWith(".xls") ||
      name.endsWith(".ods") ||
      name.endsWith(".csv") ||
      name.endsWith(".tsv") ||
      name.endsWith(".txt") ||
      name.endsWith(".json");

    if (!isSupported) {
      toast({
        title: "Unsupported File Format",
        description: "Please select an Excel (.xlsx, .xls), CSV (.csv), TSV (.tsv), or JSON (.json) file.",
        variant: "destructive",
      });
      return;
    }

    setFile(f);
    setIsReadingFile(true);
    try {
      const rows = await parseUniversalFile(f);
      if (!rows || rows.length === 0) {
        toast({
          title: "Empty Spreadsheet / File",
          description: "No data rows were found in the uploaded file.",
          variant: "destructive",
        });
        setRawRows([]);
        return;
      }
      setRawRows(rows);
      toast({
        title: "File Loaded",
        description: `Loaded ${rows.length} rows from ${f.name}.`,
      });
    } catch (err) {
      console.error("Error reading file:", err);
      toast({
        title: "Failed to Read File",
        description: err.message || "Could not read file.",
        variant: "destructive",
      });
      setRawRows([]);
    } finally {
      setIsReadingFile(false);
    }
  };

  // Parse and validate rows
  const { parsedRows, normalizedRows, errorsList, validCount } = useMemo(() => {
    if (!rawRows || rawRows.length === 0) {
      return { parsedRows: [], normalizedRows: [], errorsList: [], validCount: 0 };
    }

    const normalized = [];
    const errors = [];

    rawRows.forEach((row, idx) => {
      const mapped = mapRowHeaders(row, schema.aliases);
      // Fill in unmapped raw keys as fallback
      Object.keys(row).forEach((k) => {
        if (mapped[k] === undefined) mapped[k] = row[k];
      });

      const normItem = schema.normalize ? schema.normalize(mapped) : mapped;
      const rowErrors = schema.validate ? schema.validate(normItem) : [];

      if (rowErrors.length > 0) {
        errors.push({
          rowNumber: idx + 2, // 1-indexed including header
          item: normItem,
          raw: row,
          errors: rowErrors,
        });
      }

      normalized.push({
        _rowNumber: idx + 2,
        _valid: rowErrors.length === 0,
        _errors: rowErrors,
        ...normItem,
      });
    });

    return {
      parsedRows: rawRows,
      normalizedRows: normalized,
      errorsList: errors,
      validCount: normalized.filter((r) => r._valid).length,
    };
  }, [rawRows, schema]);

  // Handle final submission
  const handleExecuteImport = async () => {
    if (validCount === 0) {
      toast({
        title: "No Valid Records",
        description: "Please resolve column or formatting errors before importing.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Filter valid items
      const validItems = normalizedRows
        .filter((r) => r._valid)
        .map(({ _rowNumber, _valid, _errors, ...rest }) => rest);

      if (onImport) {
        await onImport(validItems, parsedRows, importMode);
      }

      toast({
        title: "Import Successful!",
        description: `Successfully imported ${validItems.length} records into ${schema.title}.`,
      });
      handleReset();
      onClose();
    } catch (err) {
      toast({
        title: "Import Error",
        description: err.message || "Failed to complete import.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl max-w-4xl w-full my-6 overflow-hidden shadow-2xl border border-gray-100 flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 via-white to-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-gray-900 tracking-tight">
                  {customTitle || `Import Spreadsheet: ${schema.title}`}
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                  Excel / CSV / JSON
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {schema.description}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Quick Guide & Sample Template Download banner */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-blue-50 text-blue-600 shrink-0">
                <Info className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-800">
                  Recommended: Download our pre-formatted template
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Includes required columns ({schema.columns.filter((c) => c.required).map((c) => c.label || c.key).join(", ")}) and example rows.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <button
                type="button"
                onClick={handleDownloadExcelTemplate}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-800 bg-emerald-100/90 hover:bg-emerald-200 border border-emerald-300 rounded-xl transition-all shadow-sm"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" />
                Download Excel (.xlsx)
              </button>
              <button
                type="button"
                onClick={handleDownloadCsvTemplate}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all shadow-sm"
              >
                <Download className="w-3.5 h-3.5" />
                Download CSV (.csv)
              </button>
            </div>
          </div>

          {/* Drag & Drop Upload Zone */}
          {isReadingFile ? (
            <div className="border-2 border-dashed border-emerald-400 bg-emerald-50/40 rounded-3xl p-12 text-center flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
              <p className="text-sm font-bold text-gray-800">Parsing Spreadsheet & Columns...</p>
              <p className="text-xs text-gray-500">Reading worksheet contents and matching fields</p>
            </div>
          ) : !file ? (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-all duration-200 flex flex-col items-center justify-center gap-3 ${
                dragOver
                  ? "border-emerald-500 bg-emerald-50/50 scale-[0.99]"
                  : "border-gray-200 hover:border-emerald-400 hover:bg-gray-50/70"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.ods,.csv,.tsv,.txt,.json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,text/plain,application/json"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shadow-inner">
                <UploadCloud className="w-7 h-7" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">
                  Drag and drop your spreadsheet or data file here, or{" "}
                  <span className="text-emerald-600 underline">browse computer</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supports Microsoft Excel, Google Sheets, LibreOffice, CSV, TSV, and JSON formats
                </p>
                <div className="flex items-center justify-center gap-1.5 mt-2.5 flex-wrap">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">.XLSX</span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">.XLS</span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">.CSV</span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">.TSV</span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">.JSON</span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-gray-50 text-gray-600 border border-gray-200">.ODS</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 bg-gray-50 border border-gray-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                    <FileSpreadsheet className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold text-gray-900 truncate max-w-xs sm:max-w-md">
                      {file.name}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB • {parsedRows.length} rows found
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-red-600 bg-white border border-gray-200 hover:border-red-200 rounded-xl transition-colors"
                  >
                    Change File
                  </button>
                </div>
              </div>

              {/* Status & Validation summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-slate-200/70 text-slate-700">
                    <Table className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-base font-black text-gray-900">{parsedRows.length}</div>
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      Total Rows
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-emerald-200/70 text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-base font-black text-emerald-900">{validCount}</div>
                    <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">
                      Ready to Import
                    </div>
                  </div>
                </div>

                <div className={`p-3 rounded-2xl flex items-center gap-3 border ${
                  errorsList.length > 0
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-gray-50 border-gray-200 text-gray-600"
                }`}>
                  <div className={`p-2 rounded-xl ${
                    errorsList.length > 0 ? "bg-amber-200/70 text-amber-700" : "bg-gray-200 text-gray-500"
                  }`}>
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-base font-black">{errorsList.length}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
                      Issues / Incomplete
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Warning Accordion (if any) */}
              {errorsList.length > 0 && (
                <div className="p-3.5 bg-amber-50/70 border border-amber-200 rounded-2xl text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-amber-800 font-bold">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <span>{errorsList.length} row(s) have missing required columns</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAllErrors(!showAllErrors)}
                      className="text-[11px] font-bold text-amber-700 hover:underline"
                    >
                      {showAllErrors ? "Hide Details" : "View Row Issues"}
                    </button>
                  </div>
                  {showAllErrors && (
                    <div className="mt-2.5 max-h-36 overflow-y-auto space-y-1.5 pt-2 border-t border-amber-200/60 font-mono text-[11px]">
                      {errorsList.slice(0, 20).map((err, i) => (
                        <div key={i} className="text-amber-900 bg-amber-100/50 p-1.5 rounded-lg">
                          <strong>Row {err.rowNumber}:</strong> {err.errors.join(", ")}
                        </div>
                      ))}
                      {errorsList.length > 20 && (
                        <p className="text-[10px] text-amber-700 italic">
                          ...and {errorsList.length - 20} more rows with warnings.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Data Preview Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-black text-gray-800 tracking-wider uppercase flex items-center gap-1.5">
                    <Table className="w-3.5 h-3.5 text-gray-500" />
                    Data Preview (First 5 Rows)
                  </h4>
                  <span className="text-[11px] text-gray-400">
                    Scroll horizontally to inspect all parsed columns
                  </span>
                </div>
                <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm max-h-56 overflow-y-auto overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase tracking-wider">Status</th>
                        {schema.columns.slice(0, 7).map((col) => (
                          <th key={col.key} className="px-3 py-2 text-left font-bold text-gray-700 whitespace-nowrap">
                            {col.label}
                            {col.required && <span className="text-red-500 ml-0.5">*</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {normalizedRows.slice(0, 5).map((row, rIdx) => (
                        <tr key={rIdx} className={row._valid ? "hover:bg-gray-50" : "bg-amber-50/30"}>
                          <td className="px-3 py-2 text-gray-400 font-mono text-[11px]">{row._rowNumber}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {row._valid ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <Check className="w-2.5 h-2.5" /> Valid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                Issue
                              </span>
                            )}
                          </td>
                          {schema.columns.slice(0, 7).map((col) => {
                            const val = row[col.key];
                            const display = typeof val === "object" ? JSON.stringify(val) : String(val || "");
                            return (
                              <td key={col.key} className="px-3 py-2 text-gray-700 max-w-[180px] truncate">
                                {display || <span className="text-gray-300 italic">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Import Mode Switch (Append vs Replace) */}
              {allowModeSwitch && (
                <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-2xl">
                  <span className="text-xs font-bold text-gray-800 block mb-2">Import Strategy</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        importMode === "append"
                          ? "bg-white border-emerald-500 shadow-sm"
                          : "bg-transparent border-gray-200 hover:bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="importMode"
                        value="append"
                        checked={importMode === "append"}
                        onChange={() => setImportMode("append")}
                        className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900">Append / Merge</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          Add new records and update existing matching records without deleting other entries.
                        </div>
                      </div>
                    </label>

                    <label
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        importMode === "replace"
                          ? "bg-white border-red-500 shadow-sm"
                          : "bg-transparent border-gray-200 hover:bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="importMode"
                        value="replace"
                        checked={importMode === "replace"}
                        onChange={() => setImportMode("replace")}
                        className="mt-0.5 text-red-600 focus:ring-red-500"
                      />
                      <div>
                        <div className="text-xs font-bold text-red-900">Replace / Overwrite</div>
                        <div className="text-[11px] text-red-600/80 mt-0.5">
                          Replace all existing entries in this section with the rows in this file.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/70 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {file ? (
              <span>
                Ready to import <strong>{validCount}</strong> of <strong>{parsedRows.length}</strong> items
              </span>
            ) : (
              <span>Select an Excel, CSV, TSV, or JSON file to preview records</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleExecuteImport}
              disabled={!file || validCount === 0 || isProcessing}
              className={`flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-xl transition-all shadow-md ${
                !file || validCount === 0 || isProcessing
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                  : importMode === "replace"
                  ? "bg-red-600 hover:bg-red-700 text-white shadow-red-200"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200"
              }`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <UploadCloud className="w-3.5 h-3.5" />
                  Import {validCount > 0 ? `${validCount} Records` : "Data"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvUploadModal;
